import { Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { Endpoint, PageConfig } from '../interfaces/internal';

import { IRepRosterSalesRep } from '../models/repRoster.model';
import {
  IVendorSalesRep,
  VendorSalesRep,
} from '../models/vendorSalesRep.model';
import { Result } from '../interfaces/helpers';
import { PageTitles } from '../interfaces/types';
import { Page } from './page';

export class TeamSalesRepPage extends Page<VendorSalesRep> {
  constructor({ endpoint }: Endpoint, config: PageConfig) {
    super(endpoint as string, PageTitles.vendorSalesRep, config);

    this.initializeColumns({
      keys: new VendorSalesRep(),
      hiddenFormFields: this.hiddenFormFields,
    });
  }

  get actions(): string[] {
    return [];
  }

  get hiddenFormFields(): string[] {
    return [
      'name',
      'manager',
      'profile',
      'vendorGroupVendor',
      'vendorManagerCPMS',
    ];
  }

  fetch(queryParams?, condition?): Observable<Result<VendorSalesRep>> {
    let id;
    let whereClause = VendorSalesRep.whereClauses.vendorGroup;
    let whereOperator = condition?.whereOperator || 'equals';
    if (queryParams) {
      id = queryParams ? queryParams.id : '';
    } else if (condition) {
      if (condition.condition.indexOf('self') !== -1) {
        whereClause = VendorSalesRep.whereClauses.cpmsId;
      } else if (condition.condition.indexOf('cpmsIDs') !== -1) {
        whereClause = VendorSalesRep.whereClauses.cpmsId;
        whereOperator = 'contains';
      } else if (condition.condition.indexOf('store') !== -1) {
        whereClause = VendorSalesRep.whereClauses.storeId;
      } else if (condition.condition.indexOf('vendorAccountName') !== -1) {
        whereClause = VendorSalesRep.whereClauses.vendorAccountName;
      } else if (condition.condition.indexOf('vendorGroupId') !== -1) {
        whereClause = VendorSalesRep.whereClauses.vendorGroup;
      } else if (condition.condition.indexOf('federationId') !== -1) {
        whereClause = VendorSalesRep.whereClauses.fedId;
      } else if (condition.condition.indexOf('name') !== -1) {
        whereClause = VendorSalesRep.whereClauses.name;
      }

      id = condition.value;
    }

    const salesRepParams = {
      q: VendorSalesRep.getVendorSalesRepQuery(id, whereClause, whereOperator),
    };

    return this.getItems<Result<VendorSalesRep>>(salesRepParams).pipe(
      tap(({ items }) => {
        this.pageItems.next(items);
      })
    );
  }

  fetchById(id: string): Observable<VendorSalesRep> {
    return this.getItem(id).pipe(
      map((data: IRepRosterSalesRep) => VendorSalesRep.createFromDTO(data))
    );
  }

  update(item: IVendorSalesRep): Observable<VendorSalesRep> {
    let updatedTeams: VendorSalesRep[];
    return this.items.pipe(
      take(1),
      switchMap((teams) => {
        if (!teams || teams.length <= 0) {
          return this.fetch().pipe(map(({ items }) => items));
        } else {
          return of(teams);
        }
      }),
      switchMap((teams) => {
        const teamIndex = teams.findIndex((i) => i.id === item.id);
        updatedTeams = [...teams];
        const oldTeam = updatedTeams[teamIndex];
        updatedTeams[teamIndex] = new VendorSalesRep({
          ...item,
          id: oldTeam.id,
        });
        return this.updateItems(updatedTeams[teamIndex]);
      }),
      tap(() => {
        this.pageItems.next(updatedTeams);
      })
    );
  }

  createFromDTO(item): VendorSalesRep {
    return VendorSalesRep.createFromDTO(item);
  }
}
